import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Phone, Mail, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { COMPANY_INFO } from '../data/siteData';
import { useServices } from '../context/ServicesContext';
import { Toaster, toast } from 'sonner';

import { API } from '../config/api';

export default function Contact() {
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get('service') || '';
  const { services } = useServices();

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: preselectedService,
    message: '',
    preferred_date: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (preselectedService) {
      setForm((f) => ({ ...f, service: preselectedService }));
    }
  }, [preselectedService]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service: form.service || 'General Inquiry',
        message: form.message,
        preferred_date: form.preferred_date ? format(form.preferred_date, 'yyyy-MM-dd') : null,
      });
      setSubmitted(true);
    } catch (e) {
      toast.error('Failed to submit. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="contact-success" className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">Thank You!</h2>
          <p className="text-slate-500 mb-6">
            We've received your request and will get back to you within 24 hours with a free estimate.
          </p>
          <Link
            to="/"
            data-testid="contact-back-home"
            className="bg-sky-700 text-white font-semibold rounded-full px-8 py-3 hover:bg-sky-600 transition-colors inline-block"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      <Toaster position="top-right" richColors />

      {/* Hero */}
      <section className="bg-sky-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-black text-slate-900 mb-4">
            Get In Touch
          </h1>
          <p className="text-slate-500 text-base max-w-lg mx-auto">
            Fill out the form below and we'll get back to you with a free estimate.
          </p>
        </div>
      </section>

      {/* Form section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1.5">Full Name *</label>
                    <Input
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1.5">Email *</label>
                    <Input
                      type="email"
                      data-testid="contact-email-input"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1.5">Phone *</label>
                    <Input
                      type="tel"
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(239) 555-0123"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1.5">Service (optional)</label>
                    <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                      <SelectTrigger data-testid="contact-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((s) => (
                          <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1.5">Preferred Date (optional)</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="contact-date-picker"
                        className="w-full justify-start text-left font-normal"
                      >
                        {form.preferred_date
                          ? format(form.preferred_date, 'PPP')
                          : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.preferred_date}
                        onSelect={(d) => setForm({ ...form, preferred_date: d })}
                        disabled={(date) => date < new Date()}
                        data-testid="contact-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1.5">Message *</label>
                  <Textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your property and what services you're interested in..."
                    rows={5}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="bg-sky-700 text-white font-semibold rounded-full px-8 py-3 hover:bg-sky-600 w-full sm:w-auto"
                >
                  {submitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Request
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact info sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32">
                <div className="bg-sky-50 rounded-2xl border border-sky-100 p-6">
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-5">Contact Info</h3>
                  <div className="space-y-4">
                    <a
                      href={`tel:${COMPANY_INFO.phone}`}
                      data-testid="contact-sidebar-phone"
                      className="flex items-center gap-3 text-slate-600 hover:text-sky-700 transition-colors"
                    >
                      <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                        <Phone className="w-5 h-5 text-sky-700" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Phone</p>
                        <p className="font-medium text-sm">{COMPANY_INFO.phone}</p>
                      </div>
                    </a>
                    <a
                      href={`mailto:${COMPANY_INFO.email}`}
                      data-testid="contact-sidebar-email"
                      className="flex items-center gap-3 text-slate-600 hover:text-sky-700 transition-colors"
                    >
                      <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                        <Mail className="w-5 h-5 text-sky-700" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Email</p>
                        <p className="font-medium text-sm">{COMPANY_INFO.email}</p>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
